import React, { useCallback, useEffect, useState } from 'react'
import { Drawer, Checkbox, Row, Col, Button, Select } from 'antd'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { browserName, CustomView } from 'react-device-detect'
import MultiSelect from '@commonComponents/MultiSelect'
import Icons from '@commonComponents/Icons'
import { Document, Image, Page, PDFDownloadLink, PDFViewer, Text, View, Font } from '@react-pdf/renderer'
import * as moment from 'moment'
import { formatMoney } from 'accounting-js'
import { formatHourWithUtc } from '../../../../../utils/dates'
import { standardDate } from '../../../flag-management.tools'
import RobotoRegular from '../../../../../assets/fonts/Roboto-Regular.ttf'
import RobotoBold from '../../../../../assets/fonts/Roboto-Bold.ttf'
import { DECIMALS_MONEY } from '../../../../../refactory/utils/moneyFormat'
import { useGate } from 'statsig-react'
import { ScrollIcon } from '@phosphor-icons/react'
import { getState, PRINT_FORMAT_OPTIONS, STATES, styleSheet as styles } from './PrintProductList.constants'
import { PrintProductList80mm } from './PrintProductList80mm.component'

const CheckboxGroup = Checkbox.Group

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: RobotoRegular,
      fontStyle: 'normal',
      fontWeight: 'normal'
    },
    {
      src: RobotoBold,
      fontStyle: 'normal',
      fontWeight: 'bold'
    }
  ]
})

// Create Document Component
const PDFProductList = ({
  job,
  items,
  selectedCategories,
  selectedStates,
  copy,
  clientLogoURL,
  categoriesSize,
  packages,
  selectedFormat
}) => {
  const {
    jobNumber,
    origin: { name: originName },
    storeTzCreatedAt,
    receiver: { firstName: clientFirstName, lastName: clientLastName, phoneNumber, email, identification },
    destination: { address, addressTwo },
    paymentMethod,
    storeTzSlotFrom,
    storeTzSlotTo,
    logisticCostCurrency,
    externalData
  } = job

  const symbolValue = {
    symbol: `${logisticCostCurrency} `,
    format: '%v %s',
    precision: DECIMALS_MONEY[logisticCostCurrency] || DECIMALS_MONEY.default
  }
  const generatedAt = moment().format('DD/MM/YYYY · LT')
  const hasIdentification =
    (typeof identification === 'string' && identification.replaceAll(' ', '').length > 0) ||
    (identification?.type && identification?.number)
      ? true
      : false
  const posOrderId = externalData?.backoffice?.[`Pos order id`] || externalData?.backoffice?.pos_order_id

  const itemsCount =
    packages &&
    packages.reduce((sum, { categories, package: _package }) => {
      return (
        sum +
        (categories && Object.keys(categories).length
          ? Object.keys(categories)
              .map((ct) => categories[ct].length)
              .reduce((sum, length) => sum + length)
          : 0)
      )
    }, 0)

  const getRelatedItem = (item) => {
    const { itemId, isSubstitute, replacedBy } = item

    const itemReplacedBy = replacedBy ? items.find(({ itemId: id }) => id === replacedBy) : null
    const itemSubstituteOf = isSubstitute ? items.find(({ replacedBy: id }) => id === itemId) : null

    return {
      itemReplacedBy,
      itemSubstituteOf
    }
  }

  const PaintItem = ({ item }) => {
    const {
      name,
      reference,
      attributes: { ean },
      state,
      presentation,
      presentationV2,
      foundQuantity,
      comment,
      rejected: { rejectedByDriver, rejectedByUser }
    } = item

    const unitPrice = `${formatMoney(presentationV2 ? presentationV2.price : presentation.price.amount, symbolValue)}`

    let quantity = foundQuantity
    if (state === STATES.REJECTED) {
      quantity = rejectedByDriver + rejectedByUser
    }

    const itemQuantity = ` ${quantity} ${copy('jobs.job.info.items-view.product-card.from')} ${
      presentationV2
        ? `${presentationV2.quantity} ${presentationV2.unit}`
        : `${presentation.quantity} ${presentation.unit}`
    } `

    const { itemReplacedBy, itemSubstituteOf } = getRelatedItem(item)

    return (
      <View>
        <View style={{ ...styles.row, marginTop: 10, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
          <View style={{ ...styles.col, paddingLeft: 10 }}>
            <Text style={styles.text}>{name}</Text>
            <Text style={styles.textSKU}>{item.attributes.sku}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.center }}>{reference}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.center }}>{(ean || '').replace(/,/gi, ', ')}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.right }}>{itemQuantity}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.right }}>{unitPrice}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.center }}>
              {copy(`jobs.job.info.items-view.print.states.${getState(state).toLocaleLowerCase()}`)}
            </Text>
          </View>
        </View>
        {itemReplacedBy && itemReplacedBy.name ? (
          <View style={{ ...styles.row, marginTop: 3, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
            <View style={{ ...styles.col, paddingLeft: 10 }}>
              <Text style={styles.smallText}>{`${copy('replacedBy')}: “${itemReplacedBy.name}”`}</Text>
            </View>
          </View>
        ) : null}
        {itemSubstituteOf && itemSubstituteOf.name ? (
          <View style={{ ...styles.row, marginTop: 3, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
            <View style={{ ...styles.col, paddingLeft: 10 }}>
              <Text style={styles.smallText}>{`${copy('replaceTo')}: “${itemSubstituteOf.name}”`}</Text>
            </View>
          </View>
        ) : null}
        {comment ? (
          <View style={{ ...styles.row, marginTop: 3, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
            <View style={{ ...styles.col, paddingLeft: 10 }}>
              <Text style={{ ...styles.text, fontFamily: 'Helvetica-Oblique' }}>{`“${comment}”`}</Text>
            </View>
          </View>
        ) : null}
      </View>
    )
  }

  const PaintCategory = ({ categories, category, marginTop }) => {
    return (
      <>
        <View style={{ ...styles.row, marginTop, paddingLeft: 10, paddingRight: 10 }}>
          <View style={styles.col}>
            <Text
              style={{ ...styles.bold, textTransform: 'capitalize' }}
            >{`${category} (${categories[category].length})`}</Text>
          </View>
        </View>
        {categories[category] &&
          categories[category].map((item, idx) => <PaintItem item={item} key={idx.toString()} />)}
      </>
    )
  }

  const paymentValue = formatMoney(
    job?.paymentInfo?.prices?.orderValue !== job.updatedPrices?.paymentValue
      ? job?.paymentInfo?.prices?.orderValue
      : job.updatedPrices?.paymentValue,
    symbolValue
  )

  if (selectedFormat === '80mm') {
    return (
      <PrintProductList80mm
        job={job}
        clientLogoURL={clientLogoURL}
        itemsCount={itemsCount}
        paymentValue={paymentValue}
        packages={packages}
      />
    )
  }

  return (
    <Document title={jobNumber}>
      <Page size='A4' style={styles.page} wrap>
        <View style={styles.row}>
          <View style={styles.brand}>
            {clientLogoURL !== '' && <Image src={clientLogoURL} style={styles.logoImage} />}
            <Image src='/powered-by.png' style={styles.powered} />
          </View>
          <View style={styles.headerCol}>
            <View style={{ ...styles.textRow, marginBottom: 30 }}>
              <Text style={{ ...styles.detailsLabel, flex: 0 }}>
                {copy('jobs.job.info.items-view.print.labels.details')}
              </Text>
              <Text style={{ ...styles.detailsText, flex: 0 }}>{jobNumber}</Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.origin')}</Text>
              <Text style={styles.detailsText}>{originName}</Text>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.destination')}</Text>
              <Text style={styles.detailsText}>{address}</Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.createdAt')}</Text>
              <Text style={styles.detailsText}>{`${moment(formatHourWithUtc(standardDate(storeTzCreatedAt))).format(
                'DD/MM/YYYY LT'
              )}`}</Text>
              <Text style={styles.detailsLabel}>
                {copy('jobs.job.info.items-view.print.labels.destination-detail')}
              </Text>
              <Text style={styles.detailsText}>{addressTwo}</Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.client')}</Text>
              <Text style={styles.detailsText}>{`${clientFirstName} ${clientLastName}`}</Text>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.delivery')}</Text>
              <Text style={styles.detailsText}>
                {`${moment(formatHourWithUtc(standardDate(storeTzSlotFrom))).format('DD/MM/YYYY LT')} - ${moment(
                  formatHourWithUtc(standardDate(storeTzSlotTo))
                ).format('LT')}`}
              </Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.email')}</Text>
              <Text style={styles.detailsTextLG}>{email}</Text>
            </View>
            <View style={styles.textRow}>
              {hasIdentification ? (
                <>
                  <Text style={styles.detailsLabel}>
                    {copy('jobs.job.info.items-view.print.labels.identification')}
                  </Text>
                  <Text style={styles.detailsText}>
                    {identification.type && identification.number
                      ? `${identification.type} ${identification.number}`
                      : typeof identification === 'string'
                      ? identification
                      : ''}
                  </Text>
                  <Text style={styles.detailsLabel}>
                    {copy('jobs.job.info.items-view.print.labels.payment-method')}
                  </Text>
                  <Text style={styles.detailsText}>{copy(`${paymentMethod}`)}</Text>
                </>
              ) : (
                <>
                  <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.phone')}</Text>
                  <Text style={styles.detailsText}>{phoneNumber}</Text>
                  <Text style={styles.detailsLabel}>
                    {copy('jobs.job.info.items-view.print.labels.payment-method')}
                  </Text>
                  <Text style={styles.detailsText}>{copy(`${paymentMethod}`)}</Text>
                </>
              )}
            </View>
            {hasIdentification ? (
              <View style={styles.textRow}>
                <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.phone')}</Text>
                <Text style={styles.detailsText}>{phoneNumber}</Text>
                <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.payment-value')}</Text>
                <Text style={styles.detailsText}>
                  {formatMoney(
                    job?.paymentInfo?.prices?.orderValue !== job.updatedPrices?.paymentValue
                      ? job?.paymentInfo?.prices?.orderValue
                      : job.updatedPrices?.paymentValue,
                    symbolValue
                  )}
                </Text>
              </View>
            ) : null}
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.item-list')}</Text>
              <Text style={styles.detailsText}>{`${itemsCount} ${copy(
                'jobs.job.info.items-view.print.printed'
              )}`}</Text>
              {hasIdentification ? (
                <>
                  <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.total-items')}</Text>
                  <Text style={styles.detailsText}>{`${job.items.length} ${copy(
                    'jobs.job.info.items-view.print.items-label'
                  )}`}</Text>
                </>
              ) : (
                <>
                  <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.payment-value')}</Text>
                  <Text style={styles.detailsText}>
                    {formatMoney(
                      job?.paymentInfo?.prices?.orderValue !== job.updatedPrices?.paymentValue
                        ? job?.paymentInfo?.prices?.orderValue
                        : job.updatedPrices?.paymentValue,
                      symbolValue
                    )}
                  </Text>
                </>
              )}
            </View>
            {(posOrderId || !hasIdentification) && (
              <View style={styles.textRow}>
                {!hasIdentification && (
                  <>
                    <Text style={styles.detailsLabel}>{copy('jobs.job.info.items-view.print.labels.total-items')}</Text>
                    <Text style={posOrderId ? styles.detailsText : styles.detailsTextLG}>{`${job.items.length} ${copy(
                      'jobs.job.info.items-view.print.items-label'
                    )}`}</Text>
                  </>
                )}
                {posOrderId && (
                  <>
                    <Text style={styles.detailsLabel}>Pos order ID:</Text>
                    <Text style={!hasIdentification ? styles.detailsText : styles.detailsTextLG}>{posOrderId}</Text>
                  </>
                )}
              </View>
            )}
          </View>
        </View>

        {selectedCategories && selectedCategories.length && categoriesSize > selectedCategories.length ? (
          <View
            style={{
              ...styles.row,
              marginTop: 25,
              paddingLeft: 10,
              paddingRight: 10
            }}
          >
            <View style={{ ...styles.col, maxWidth: '58px' }}>
              <Text style={{ ...styles.text, fontSize: 10 }}>{`${copy(
                'jobs.job.info.items-view.print.categories-label'
              )} · `}</Text>
            </View>
            <View style={{ ...styles.col }}>
              <Text style={{ ...styles.text, ...styles.bold, fontSize: 10 }}>{selectedCategories.join('; ')}</Text>
            </View>
          </View>
        ) : (
          <View />
        )}

        {selectedStates && selectedStates.length ? (
          <View
            style={{
              ...styles.row,
              marginTop: 10,
              paddingLeft: 10,
              paddingRight: 10
            }}
          >
            <View style={{ ...styles.col, maxWidth: '110px' }}>
              <Text style={{ ...styles.text, fontSize: 10 }}>{`${copy(
                'jobs.job.info.items-view.print.state-label'
              )} · `}</Text>
            </View>
            <View style={{ ...styles.col }}>
              <Text style={{ ...styles.text, ...styles.bold, fontSize: 10 }}>
                {selectedStates.map((st) => copy(`jobs.job.info.items-view.${st.toLocaleLowerCase()}`)).join('; ')}
              </Text>
            </View>
          </View>
        ) : (
          <View />
        )}

        <View
          style={{
            ...styles.row,
            marginTop: 25,
            paddingLeft: 10,
            paddingRight: 10
          }}
          fixed
        >
          <View style={{ ...styles.col, paddingLeft: 10 }}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {copy('jobs.job.info.items-view.print.labels.item')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {copy('jobs.job.info.items-view.print.labels.reference')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {copy('jobs.job.info.items-view.print.labels.ean')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {copy('jobs.job.info.items-view.print.labels.quantity')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {copy('jobs.job.info.items-view.print.labels.unit-price')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {copy('jobs.job.info.items-view.print.labels.state')}
            </Text>
          </View>
        </View>
        {packages &&
          packages.map(({ categories, package: _package }, packageIdx) => {
            return (
              <View key={`package-${packageIdx}`}>
                {_package && Object.keys(categories).length > 0 ? (
                  <View style={{ backgroundColor: '#F9F9F9', marginTop: 8 }}>
                    <Text style={styles.package}>
                      {copy('jobs.job.info.items-view.print.labels.package', _package)}
                    </Text>
                  </View>
                ) : null}
                {Object.keys(categories).map((key, idx) => (
                  <PaintCategory
                    categories={categories}
                    category={key}
                    key={idx.toString()}
                    marginTop={_package ? 8 : 15}
                  />
                ))}
              </View>
            )
          })}

        <View style={styles.footer} fixed>
          <Text>{`${copy('jobs.job.info.items-view.print.generated-at')}: ${generatedAt}`}</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} ${copy('jobs.job.info.items-view.product-card.from')} ${totalPages}`
            }
            fixed
          />
        </View>

        <View
          style={{ ...styles.row, ...styles.headerMore }}
          fixed
          render={({ pageNumber }) => {
            if (pageNumber === 1) {
              return
            }

            return (
              <View style={{ ...styles.textRow }}>
                <Text style={{ ...styles.detailsLabel, flex: 0 }}>Detalle de la orden:</Text>
                <Text style={{ ...styles.detailsText, flex: 0 }}>{jobNumber}</Text>
              </View>
            )
          }}
        />
      </Page>
    </Document>
  )
}

const PrintProductList = ({ job, items, state, clientLogoURL, ...props }) => {
  const { t } = useTranslation()
  const [, updateState] = useState()
  const [selectedStates, setSelectedStates] = useState([state])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [categories, setCategories] = useState([])
  const [packageList, setPackageList] = useState([])
  const [showPDF, setShowPDF] = useState(false)
  const [states, setStates] = useState([])
  const [isChrome] = useState(browserName === 'Chrome')
  const [printDisabled, setPrintDisabled] = useState(true)
  const forceRender = useCallback(() => updateState({}), [])
  const [selectedFormat, setSelectedFormat] = useState(PRINT_FORMAT_OPTIONS[0].value)

  const handleChangeSelectedFormat = (value) => {
    setShowPDF(false)
    setSelectedFormat(value)
  }

  const is80mmPrintActive = useGate('is_80mm_print_active').value

  const createPackagesMap = () => {
    const createCategoriesMap = (items) => {
      const newCategoriesMap = {}
      if (items && items.length) {
        items.map((item) => {
          let {
            attributes: { category }
          } = item
          category = category || t('jobs.job.info.items-view.print.no-category')

          if (
            (selectedCategories.length === 0 || selectedCategories.includes(category)) &&
            (selectedStates.length === 0 || selectedStates.includes(getState(item.state)))
          ) {
            if (!newCategoriesMap[category]) {
              newCategoriesMap[category] = []
            }
            newCategoriesMap[category].push(item)
          }

          return category
        })
      }
      return newCategoriesMap
    }

    let newPackagesMap = []
    if (items && items.length) {
      if (job.packages && job.packages.length > 0) {
        newPackagesMap = [null, ...job.packages].map((_package) => {
          return {
            package: _package,
            categories: createCategoriesMap(
              items.filter(
                (item) =>
                  _package === item.packageReference || (_package && item.packageReference === _package.reference)
              )
            )
          }
        })
      } else {
        newPackagesMap.push({
          package: null,
          categories: createCategoriesMap(items)
        })
      }
    }
    return newPackagesMap
  }

  const getMultiSelectText = (_) => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return t('jobs.job.info.items-view.print.multiSelect.buttonText.nothing')
    } else if (categories.length === selectedCategories.length) {
      return t('jobs.job.info.items-view.print.multiSelect.buttonText.all')
    } else {
      return t('jobs.job.info.items-view.print.multiSelect.buttonText.some', {
        quantity: `${selectedCategories.length}/${categories.length}`
      })
    }
  }

  const onChangeStates = (_states) => {
    setShowPDF(false)
    setSelectedStates(_states)
  }

  const onChangeCategories = (_categories) => {
    setShowPDF(false)
    setSelectedCategories(_categories)
    setPrintDisabled(_categories.length === 0)
    forceRender()
  }

  const createPDF = () => {
    setShowPDF(false)
    setPackageList(createPackagesMap(job?.packages))
    setShowPDF(true)
  }

  useEffect(() => {
    const updateCategories = () => {
      let newCategories = []
      if (items && items.length) {
        newCategories = Array.from(
          new Set(
            items.map((item) => {
              let {
                attributes: { category }
              } = item
              category = category || t('jobs.job.info.items-view.print.no-category')
              return category
            })
          )
        ).map((name) => {
          return { label: name, value: name }
        })
      }

      setCategories(newCategories)
    }

    const updateStates = () => {
      let newStates = []
      if (items && items.length) {
        newStates = Array.from(
          new Set(
            items.map((item) => {
              const { state: st } = item
              return getState(st)
            })
          )
        ).map((name) => {
          return {
            label: t(`jobs.job.info.items-view.${name.toLocaleLowerCase()}`),
            value: name
          }
        })
      }

      setStates(newStates)
    }

    const reset = () => {
      setSelectedStates([state])
      setSelectedCategories([])
      setShowPDF(false)
      setPrintDisabled(true)
      setPackageList(createPackagesMap(job?.packages))
    }

    reset()
    updateCategories()
    updateStates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, props.visible, state])

  return (
    <Drawer {...props}>
      <div className='print-product-list'>
        <p>{t('jobs.job.info.items-view.print.text')}</p>

        <h3>
          <span className='print-product-list-icon'>
            <Icons iconName='Category' />
          </span>{' '}
          {t('jobs.job.info.items-view.print.categories-label')}
        </h3>
        <MultiSelect
          className='print-product-list-multiselect'
          buttonText={getMultiSelectText()}
          checkListTitle={t('jobs.job.info.items-view.print.multiSelect.categories')}
          searchText={t('jobs.job.info.items-view.print.multiSelect.searchText')}
          saveText={t('jobs.job.info.items-view.print.multiSelect.save')}
          onChangeSelected={onChangeCategories}
          dataSource={categories}
          dataSelected={selectedCategories}
        />

        <h3>
          <span className='print-product-list-icon'>
            <Icons iconName='Loader' />
          </span>{' '}
          {t('jobs.job.info.items-view.print.state-label')}
        </h3>

        <CheckboxGroup options={states} value={selectedStates} onChange={onChangeStates} />
        {is80mmPrintActive && (
          <>
            <h3>
              <span className='print-product-list-icon'>
                <ScrollIcon />
              </span>{' '}
              {t('format')}
            </h3>
            <Select
              defaultValue={PRINT_FORMAT_OPTIONS[0].value}
              style={{ width: 170 }}
              onChange={handleChangeSelectedFormat}
              options={PRINT_FORMAT_OPTIONS}
            />
          </>
        )}

        <Row justify={!isChrome ? 'space-between' : 'end'} className='print-product-list-action'>
          {showPDF && !isChrome ? (
            <Col>
              <PDFDownloadLink
                document={
                  <PDFProductList
                    job={job}
                    items={items}
                    packages={packageList}
                    clientLogoURL={clientLogoURL}
                    copy={t}
                    selectedCategories={selectedCategories}
                    selectedStates={selectedStates}
                    categoriesSize={categories.length}
                    selectedFormat={selectedFormat}
                  />
                }
                fileName={`${job.jobNumber}.pdf`}
              >
                {({ loading }) =>
                  loading
                    ? t('jobs.job.info.items-view.print.loading')
                    : `${t('jobs.job.info.items-view.print.download')} ${job.jobNumber}.pdf`
                }
              </PDFDownloadLink>
            </Col>
          ) : (
            ''
          )}
          <Col>
            <Button type='primary' onClick={createPDF} disabled={printDisabled}>
              {t('jobs.job.info.items-view.print.action')}
            </Button>
          </Col>
        </Row>

        {showPDF ? (
          <Row className='print-product-list-pdf'>
            <Col span={24}>
              <CustomView condition={isChrome}>
                <PDFViewer width='100%' height='600px'>
                  <PDFProductList
                    job={job}
                    items={items}
                    packages={packageList}
                    clientLogoURL={clientLogoURL}
                    copy={t}
                    selectedCategories={selectedCategories}
                    selectedStates={selectedStates}
                    categoriesSize={categories.length}
                    selectedFormat={selectedFormat}
                  />
                </PDFViewer>
              </CustomView>
            </Col>
          </Row>
        ) : (
          ''
        )}
      </div>
    </Drawer>
  )
}

PrintProductList.propTypes = {
  items: PropTypes.array.isRequired,
  state: PropTypes.oneOf([STATES.ADDED, STATES.PENDING, STATES.REMOVED]),
  onClose: PropTypes.func
}

PrintProductList.defaultProps = {
  items: [],
  state: STATES.PENDING,
  onClose: () => {}
}

export default PrintProductList
